import { OTLayout } from "@/components/layout/ot-layout";
import { useState, useEffect, useCallback } from "react";
import { BookOpen, ChevronDown, ChevronUp, Save, Loader2 } from "lucide-react";
import { Link } from "wouter";

const RCOT_DIMENSIONS = [
  {
    key: "breadth",
    title: "Breadth",
    subtitle: "Range of CPD activities",
    description: "Demonstrates engagement with a diverse range of CPD activities across different formats, settings, and professional areas.",
    examples: [
      "Attending RCOT Annual Conference and SS Rheumatology study days",
      "Online eLearning modules (OTnews, RCOT CPD resources)",
      "Reading occupational therapy and rheumatology journals",
      "Peer supervision, case discussion, and reflective practice groups",
      "Simulation, skills labs, and clinical workshops",
    ],
    colour: "amber",
    category: "educational",
    competencyKey: "breadth-dimension",
    stageKey: "rcot-cpd",
  },
  {
    key: "depth",
    title: "Depth",
    subtitle: "Specialist knowledge development",
    description: "Demonstrates sustained, in-depth development of specialist knowledge and skills relevant to rheumatology OT practice.",
    examples: [
      "Postgraduate study in rheumatology or MSK OT",
      "RCOT SS Rheumatology specialist competency development",
      "Advanced hand therapy or splinting training",
      "AMPS, COPM, or DASH training and standardisation",
      "Joint protection programme (JPPE) training and delivery",
    ],
    colour: "orange",
    category: "clinical",
    competencyKey: "depth-dimension",
    stageKey: "rcot-cpd",
  },
  {
    key: "impact",
    title: "Impact",
    subtitle: "Evidence of improved practice",
    description: "Demonstrates how CPD has directly improved occupational therapy practice and benefited the people you work with.",
    examples: [
      "Service evaluation demonstrating improved patient outcomes post-CPD",
      "Changes to clinical practice following evidence review or training",
      "Contribution to service development or quality improvement projects",
      "Supervision or teaching of colleagues based on specialist learning",
      "Patient feedback demonstrating improved occupational performance",
    ],
    colour: "rose",
    category: "professional",
    competencyKey: "impact-dimension",
    stageKey: "rcot-cpd",
  },
];

const COLOUR_MAP: Record<string, { bg: string; border: string; text: string; badge: string; bar: string; ring: string }> = {
  amber: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", badge: "bg-amber-100 text-amber-700", bar: "bg-amber-500", ring: "ring-amber-400" },
  orange: { bg: "bg-orange-50", border: "border-orange-200", text: "text-orange-800", badge: "bg-orange-100 text-orange-700", bar: "bg-orange-500", ring: "ring-orange-400" },
  rose: { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-800", badge: "bg-rose-100 text-rose-700", bar: "bg-rose-500", ring: "ring-rose-400" },
};

type Status = "developing" | "achieving" | "achieved";

const STATUS_LABELS: Record<Status, string> = {
  developing: "Developing",
  achieving: "Achieving",
  achieved: "Achieved",
};

const STATUS_COLORS: Record<Status, string> = {
  developing: "bg-amber-50 text-amber-700 border-amber-200",
  achieving: "bg-blue-50 text-blue-700 border-blue-200",
  achieved: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

interface CpdActivity {
  id: number;
  title: string;
  date: string;
  category: string;
  hours: number;
}

interface DimensionEntry {
  status: Status;
  evidenceNotes: string;
}

type DimensionEntries = Record<string, DimensionEntry>;

export default function RcotFrameworkTracker() {
  const [activities, setActivities] = useState<CpdActivity[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [dimensionEntries, setDimensionEntries] = useState<DimensionEntries>({});
  const [saving, setSaving] = useState<string | null>(null);

  const loadPortfolio = useCallback(async () => {
    const res = await fetch("/api/ot/portfolio");
    if (res.ok) {
      const data: { frameworkKey: string; stageKey: string; competencyKey: string; status: Status; evidenceNotes: string | null }[] = await res.json();
      const entries: DimensionEntries = {};
      for (const d of data.filter(e => e.frameworkKey === "rcot-cpd")) {
        entries[d.competencyKey] = {
          status: d.status,
          evidenceNotes: d.evidenceNotes ?? "",
        };
      }
      setDimensionEntries(entries);
    }
  }, []);

  useEffect(() => {
    const year = new Date().getFullYear();
    fetch(`/api/ot/cpd?year=${year}`).then(r => r.ok ? r.json() : []).then(setActivities);
    loadPortfolio();
  }, [loadPortfolio]);

  const setStatus = (competencyKey: string, status: Status) => {
    setDimensionEntries(prev => ({
      ...prev,
      [competencyKey]: { ...prev[competencyKey] ?? { evidenceNotes: "" }, status },
    }));
  };

  const setNotes = (competencyKey: string, notes: string) => {
    setDimensionEntries(prev => ({
      ...prev,
      [competencyKey]: { ...prev[competencyKey] ?? { status: "developing" as Status }, evidenceNotes: notes },
    }));
  };

  const saveDimension = async (dim: typeof RCOT_DIMENSIONS[0]) => {
    const entry = dimensionEntries[dim.competencyKey] ?? { status: "developing" as Status, evidenceNotes: "" };
    setSaving(dim.key);
    try {
      await fetch(`/api/ot/portfolio/rcot-cpd/${dim.stageKey}/${dim.competencyKey}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          status: entry.status,
          evidenceNotes: entry.evidenceNotes || undefined,
        }),
      });
    } finally {
      setSaving(null);
    }
  };

  const byCategory = activities.reduce((acc, a) => {
    acc[a.category] = (acc[a.category] ?? 0) + a.hours;
    return acc;
  }, {} as Record<string, number>);

  const totalHours = activities.reduce((s, a) => s + a.hours, 0);

  return (
    <OTLayout title="RCOT CPD Framework Tracker">
      <div className="space-y-6">

        <div className="bg-gradient-to-br from-amber-700 to-orange-800 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-2">
            <BookOpen className="h-7 w-7 text-amber-300" />
            <div>
              <h2 className="text-xl font-bold">RCOT CPD Framework</h2>
              <p className="text-amber-200 text-sm">Three dimensions of occupational therapy CPD — with evidence tracking</p>
            </div>
          </div>
          <p className="text-amber-100 text-sm leading-relaxed">
            The RCOT CPD framework structures professional development across three interconnected dimensions: Breadth (range of activities), Depth (specialist knowledge), and Impact (evidence of improved practice). Tag your progress and save evidence notes against each dimension.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {RCOT_DIMENSIONS.map((dim) => {
            const hours = byCategory[dim.category] ?? 0;
            const pct = totalHours > 0 ? Math.round((hours / totalHours) * 100) : 0;
            const c = COLOUR_MAP[dim.colour];
            const isOpen = expanded === dim.key;
            const entry = dimensionEntries[dim.competencyKey];

            return (
              <div key={dim.key} className={`rounded-2xl border p-5 ${c.bg} ${c.border}`}>
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className={`text-lg font-bold ${c.text}`}>{dim.title}</h3>
                    {entry?.status && (
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${STATUS_COLORS[entry.status]}`}>
                        {STATUS_LABELS[entry.status]}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500">{dim.subtitle}</p>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed mb-4">{dim.description}</p>

                <div className="mb-4">
                  <div className="flex justify-between text-xs text-slate-500 mb-1">
                    <span>{hours.toFixed(1)} hrs ({dim.category})</span>
                    <span>{pct}% of total</span>
                  </div>
                  <div className="h-2 bg-white/60 rounded-full">
                    <div className={`h-2 rounded-full transition-all ${c.bar}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>

                <button
                  onClick={() => setExpanded(isOpen ? null : dim.key)}
                  className="flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-800 mb-3"
                >
                  {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  {isOpen ? "Hide detail" : "Add evidence & examples"}
                </button>

                {isOpen && (
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs font-semibold text-slate-600 mb-1.5">Dimension status</p>
                      <div className="flex flex-wrap gap-1.5">
                        {(["developing", "achieving", "achieved"] as Status[]).map((s) => (
                          <button
                            key={s}
                            onClick={() => setStatus(dim.competencyKey, s)}
                            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                              (entry?.status ?? "developing") === s
                                ? STATUS_COLORS[s] + " ring-2 ring-offset-1 ring-current"
                                : "bg-white/60 border-slate-300 text-slate-500 hover:bg-white"
                            }`}
                          >
                            {STATUS_LABELS[s]}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <p className="text-xs font-semibold text-slate-600 mb-1.5">Evidence notes</p>
                      <textarea
                        value={entry?.evidenceNotes ?? ""}
                        onChange={(e) => setNotes(dim.competencyKey, e.target.value)}
                        placeholder={`Describe how your CPD demonstrates ${dim.title.toLowerCase()} in rheumatology OT practice...`}
                        rows={3}
                        className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none bg-white/80"
                      />
                    </div>

                    <button
                      onClick={() => saveDimension(dim)}
                      disabled={saving === dim.key}
                      className="flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 px-3 py-1.5 rounded-lg transition-colors"
                    >
                      {saving === dim.key ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                      {saving === dim.key ? "Saving..." : "Save evidence"}
                    </button>

                    <div>
                      <p className="text-xs font-semibold text-slate-500 mb-1.5">Example activities</p>
                      <ul className="space-y-1">
                        {dim.examples.map((ex, j) => (
                          <li key={j} className="flex items-start gap-2 text-xs text-slate-500">
                            <span className="mt-1 h-1.5 w-1.5 rounded-full bg-slate-400 shrink-0" />
                            {ex}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-1">How to use this framework</h3>
          <p className="text-sm text-slate-600 leading-relaxed mb-4">
            Set a status and add evidence notes for each dimension to document how your CPD is balanced across Breadth, Depth, and Impact. Aim for evidence across all three dimensions over your 2-year HCPC renewal period. Use the reflection tool to record how each activity has contributed to your OT practice.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link href="/ot/cpd">
              <button className="px-4 py-2 bg-amber-700 hover:bg-amber-800 text-white rounded-xl text-sm font-medium transition-colors">
                Log CPD Activity
              </button>
            </Link>
            <Link href="/ot/cpd/reflection">
              <button className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-medium transition-colors">
                Add Reflection
              </button>
            </Link>
          </div>
        </div>

        <div className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3 leading-relaxed">
          <strong>RCOT CPD Framework:</strong> Based on the Royal College of Occupational Therapists CPD framework. Category mapping is indicative — activities may span multiple dimensions. Evidence notes are saved to your portfolio and support your HCPC audit profile.
        </div>
      </div>
    </OTLayout>
  );
}
